import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { DataProvider } from "@/lib/store";

import AppLayout from "@/components/AppLayout";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import TodayPage from "@/pages/TodayPage";
import BoardPage from "@/pages/BoardPage";
import GridPage from "@/pages/GridPage";
import TimelinePage from "@/pages/TimelinePage";
import ItemsLayout from "@/pages/ItemsLayout";
import MoneyLayout from "@/pages/money/MoneyLayout";
import MoneyOverview from "@/pages/money/MoneyOverview";
import MoneyBudgets from "@/pages/money/MoneyBudgets";
import MoneyTransactions from "@/pages/money/MoneyTransactions";
import MoneyRecurring from "@/pages/money/MoneyRecurring";
import MoneyBills from "@/pages/money/MoneyBills";
import MoneyGoals from "@/pages/money/MoneyGoals";
import MoneyRules from "@/pages/money/MoneyRules";
import MoneyReconciliation from "@/pages/money/MoneyReconciliation";
import LandingPage from "@/pages/LandingPage";
import MoneyLedger from "@/pages/money/MoneyLedger";
import HealthLayout from "@/pages/health/HealthLayout";
import HealthToday from "@/pages/health/HealthToday";
import HealthHabits from "@/pages/health/HealthHabits";
import HealthHistory from "@/pages/health/HealthHistory";
import FinancialHealth from "@/pages/FinancialHealth";
import InboxPage from "@/pages/InboxPage";
import MembersPage from "@/pages/MembersPage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AnonRoot() {
  // For anonymous visitors, land them on the marketing page instead of /login.
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/welcome" replace />;
  return null;   // authenticated flow continues via <AppLayout /> outlet
}

// Synchronously catch OAuth callback fragment before ProtectedRoute logic runs
function AppRouter() {
  const location = useLocation();
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/login" element={<GuestOnly><AuthPage mode="login" /></GuestOnly>} />
      <Route path="/signup" element={<GuestOnly><AuthPage mode="signup" /></GuestOnly>} />
      <Route path="/" element={<AppLayout />}>
        <Route index element={<TodayPage />} />
        <Route element={<ItemsLayout />}>
          <Route path="board" element={<BoardPage />} />
          <Route path="grid" element={<GridPage />} />
          <Route path="timeline" element={<TimelinePage />} />
        </Route>
        <Route path="money" element={<MoneyLayout />}>
          <Route index element={<MoneyOverview />} />
          <Route path="budgets" element={<MoneyBudgets />} />
          <Route path="bills" element={<MoneyBills />} />
          <Route path="goals" element={<MoneyGoals />} />
          <Route path="transactions" element={<MoneyTransactions />} />
          <Route path="recurring" element={<MoneyRecurring />} />
          <Route path="rules" element={<MoneyRules />} />
          <Route path="reconciliation" element={<MoneyReconciliation />} />
          <Route path="ledger" element={<MoneyLedger />} />
        </Route>
        <Route path="health" element={<HealthLayout />}>
          <Route index element={<HealthToday />} />
          <Route path="habits" element={<HealthHabits />} />
          <Route path="history" element={<HealthHistory />} />
        </Route>
        <Route path="financial-health" element={<FinancialHealth />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="members" element={<MembersPage />} />
      </Route>
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
